import { Component } from "@theme/component";
import { trapFocus, removeTrapFocus } from "@theme/focus";
import { onAnimationEnd } from "@theme/utilities";

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 *
 * @extends {Component<Refs>}
 */
class CategoryDrawerComponent extends Component {
  requiredRefs = ["details"];

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener("keyup", this.#onKeyUp);
    this.addEventListener("click", this.#onItemClick);
    this.#setupAnimatedElementListeners();
    this.#setupExtensionHeaderControls();
    this.#setupAccordionDesktopBehavior();
    document.addEventListener("click", this.#onShopCategoriesClick, true);
    
    // Listen for native toggle events on the details element
    const details = this.refs.details;
    if (details) {
      details.addEventListener("toggle", this.#onDetailsToggle);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener("keyup", this.#onKeyUp);
    this.removeEventListener("click", this.#onItemClick);
    // document.removeEventListener("click", this.#onShopCategoriesClick, true);
    
    const details = this.refs.details;
    if (details) {
      details.removeEventListener("toggle", this.#onDetailsToggle);
    }
  }

  /**
   * Handle clicks on drawer menu items
   * @param {MouseEvent} event
   */
  #onItemClick = (event) => {
    // Check if the click target is a menu item or link
    if (!(event.target instanceof Element)) return;

    const menuItem = event.target.closest(
      ".category-drawer__settings-link, .category-drawer__menu-item, a[href]"
    );

    if (!menuItem) return;

    // Don't handle clicks on buttons (like close/back buttons)
    if (menuItem instanceof HTMLButtonElement) return;

    // Extract the label text
    const labelElement =
      menuItem.querySelector(
        ".category-drawer__settings-label, .category-drawer__menu-item-text"
      ) || menuItem;

    const label = labelElement.textContent?.trim() || "";

    // Extract handle from menu item ID (e.g., "HeaderDrawer-electric-guitars" -> "electric-guitars")
    const menuItemId = menuItem.id || "";
    const handle = menuItemId.startsWith("HeaderDrawer-")
      ? menuItemId.slice("HeaderDrawer-".length)
      : "";

    if (label) {
      this.onItemClick(label, handle);
    }
  };

  /**
   * Called when any drawer item is clicked
   * @param {string} label - The label text of the clicked item
   * @param {string} handle - The handle extracted from the menu item's ID
   */
  onItemClick(label, handle) {
    // Find extension with matching id (target_label handleized)
    if (handle) {
      const extension = /** @type {HTMLElement | null} */ (
        this.querySelector(`#target-${CSS.escape(handle)}`)
      );

      console.log(extension);

      if (extension) {
        // Hide all extensions first
        this.#hideAllExtensions();
        // Show the matching extension
        extension.removeAttribute("hidden");
      }
    }
  }

  /**
   * Hide all extension elements
   */
  #hideAllExtensions() {
    /** @type {NodeListOf<HTMLElement>} */
    const extensions = this.querySelectorAll(".category-drawer__extension");
    extensions.forEach((extension) => {
      extension.setAttribute("hidden", "");
    });
  }

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== "Escape") return;

    this.#close(this.#getDetailsElement(event));
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute("open");
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest("details") ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   * @param {Event} [event]
   */
  toggle(event) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    return this.isOpen ? this.close() : this.open(event);
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {Event} [event]
   */
  open(event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector("summary");

    if (!summary) return;

    details.setAttribute("open", "");
    summary.setAttribute("aria-expanded", "true");
    requestAnimationFrame(() => details.classList.add("menu-open"));

    trapFocus(details);
    // Disable header search input when drawer opens
    requestAnimationFrame(() => this.#disableHeaderSearchInput());
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector("summary");

    if (!summary) return;

    summary.setAttribute("aria-expanded", "false");
    details.classList.remove("menu-open");

    onAnimationEnd(details, () => {
      reset(details);

      if (details === this.refs.details) {
        // Hide all extensions when the main drawer closes
        this.#hideAllExtensions();
        removeTrapFocus();
        const openDetails = this.querySelectorAll("details[open]");
        openDetails.forEach(reset);
        this.#enableHeaderSearchInput();
      } else {
        trapFocus(this.refs.details);
      }
    });
  }

  /**
   * Handle native details toggle event
   */
  #onDetailsToggle = () => {
    // Use a small delay to ensure the state has updated
    requestAnimationFrame(() => {
      this.#handleNativeToggle();
    });
  };

  /**
   * Disable the header search input to prevent focus when drawer is open
   */
  #disableHeaderSearchInput() {
    try {
      const searchInput = document.getElementById("header-search-input");
      if (searchInput) {
        // Store original tabindex if it exists
        if (!searchInput.dataset.originalTabindex) {
          searchInput.dataset.originalTabindex = searchInput.tabIndex.toString();
        }
        searchInput.setAttribute("tabindex", "-1");
        searchInput.setAttribute("aria-hidden", "true");
      }
    } catch (error) {
      // Silently fail if search input doesn't exist
      console.warn("Could not disable header search input:", error);
    }
  }

  /**
   * Re-enable the header search input when drawer closes
   */
  #enableHeaderSearchInput() {
    try {
      const searchInput = document.getElementById("header-search-input");
      if (searchInput) {
        const originalTabindex = searchInput.dataset.originalTabindex;
        if (originalTabindex !== undefined) {
          if (originalTabindex === "-1" || originalTabindex === "") {
            searchInput.removeAttribute("tabindex");
          } else {
            searchInput.setAttribute("tabindex", originalTabindex);
          }
          delete searchInput.dataset.originalTabindex;
        } else {
          searchInput.removeAttribute("tabindex");
        }
        searchInput.removeAttribute("aria-hidden");
      }
    } catch (error) {
      // Silently fail if search input doesn't exist
      console.warn("Could not enable header search input:", error);
    }
  }

  /**
   * Handle drawer state changes for native details element behavior
   * This is called when the summary is clicked directly (bypassing our open/close methods)
   */
  #handleNativeToggle() {
    // Check if drawer is open after native toggle
    if (this.isOpen) {
      this.#disableHeaderSearchInput();
    } else {
      this.#enableHeaderSearchInput();
    }
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    /**
     * @param {AnimationEvent} event
     */
    function removeWillChangeOnAnimationEnd(event) {
      const target = event.target;
      if (target && target instanceof HTMLElement) {
        target.style.setProperty("will-change", "unset");
        target.removeEventListener(
          "animationend",
          removeWillChangeOnAnimationEnd
        );
      }
    }
    const allAnimated = this.querySelectorAll(
      ".category-drawer__animated-element"
    );
    allAnimated.forEach((element) => {
      element.addEventListener("animationend", removeWillChangeOnAnimationEnd);
    });
  }

  /**
   * Wire up back/close controls in the extension header (mobile)
   */
  #setupExtensionHeaderControls() {
    this.addEventListener("click", (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;

      // Back: hide extensions, return to top-level drawer
      if (target.closest(".cde__back-button")) {
        event.preventDefault();
        this.#hideAllExtensions();
        return;
      }

      // Close: close drawer completely
      if (target.closest(".cde__close-button")) {
        event.preventDefault();
        this.close();
        return;
      }
    });
  }

  /**
   * Disable accordion toggle on desktop by preventing summary clicks
   */
  #setupAccordionDesktopBehavior() {
    this.addEventListener(
      "click",
      (event) => {
        const target = event.target;
        if (!(target instanceof Element)) return;
        const summary = target.closest(".cde__accordion > summary");
        if (!summary) return;
        // Desktop breakpoint: prevent toggling
        if (window.matchMedia("(min-width: 751px)").matches) {
          event.preventDefault();
          event.stopPropagation();
        }
      },
      true
    );
  }

  /**
   * Open the category drawer when any link with href="#shop-categories" is clicked
   */
  /**
   * @param {Event} e
   */
  #onShopCategoriesClick = (e) => {
    const target = e.target;
    if (!(target instanceof Element)) return;
    // Match by id or href="#shop-categories" (case-insensitive)
    const triggerById = target.closest("#category-drawer-trigger");
    // const href = triggerById?.getAttribute('href')?.trim().toLowerCase();
    // const isShopCategoriesHref = href === '#shop-categories';
    if (!triggerById) return;

    e.preventDefault();
    e.stopPropagation();

    // const headerDrawerDetails = /** @type {HTMLDetailsElement | null} */ (
    //   document.querySelector(
    //     "header-drawer details.menu-drawer-container[open]"
    //   )
    // );
    // if (headerDrawerDetails) {
    //   headerDrawerDetails.classList.remove("menu-open");
    //   headerDrawerDetails.removeAttribute("open");
    //   headerDrawerDetails
    //     .querySelector("summary")
    //     ?.setAttribute("aria-expanded", "false");
    // }

    // Allow the header drawer close animation to finish before opening category drawer
    // window.setTimeout(() => {
    //   debugger
    const details = document.querySelector("category-drawer-section");
    console.log(details);
    const summary = details?.querySelector("summary");
    summary?.click();
    // }, 280);
  };
}

if (!customElements.get("category-drawer-section")) {
  customElements.define("category-drawer-section", CategoryDrawerComponent);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove("menu-open");
  element.removeAttribute("open");
  element.querySelector("summary")?.setAttribute("aria-expanded", "false");
}

document.addEventListener("DOMContentLoaded", function () {
  document.querySelectorAll('.category-drawer-thumb-url').forEach(function(input) {
    const imageThumb = input.dataset.imageThumb?.trim();
    const targetId = input.dataset.targetId;
    if (imageThumb && targetId) {
      const targetEl = document.getElementById(targetId);
      if (targetEl) {
        targetEl.style.backgroundImage = `url('${imageThumb}')`;
        targetEl.style.backgroundSize = 'cover';
        targetEl.style.backgroundPosition = 'center';
      }
    }
  });
});